/**
 * Shared shapes for the help corpus.
 *
 * The corpus lives as markdown under `docs/help/<locale>/` (see `docs/help/README.md`
 * for the authoring contract). At build time it is parsed into the structures below and
 * written to `src/generated/help-index.json`, which is what the Help Center loads.
 */

/** Locale codes the corpus supports. `en` is the source locale and the fallback. */
export const SOURCE_LOCALE = 'en';

/** An article's frontmatter, after parsing and validation. */
export type HelpArticleMeta = {
    /** Stable dotted id. Append-only — also used as a page's `helpId`. */
    id: string;
    title: string;
    section: string;
    chapter?: string;
    order: number;
    /** Application path this article documents. Absent on concept articles. */
    route?: string;
    /** Set when the article documents one tab of a page. Excluded from coverage. */
    tab?: string;
    /** Permission id required to see the article at all. */
    permission?: string;
    /** Search terms per locale, e.g. `{ he: ['ביטול שירות'] }`. */
    keywords: Record<string, string[]>;
    related: string[];
    status: 'draft' | 'reviewed';
    /** ISO date of the last substantive edit. Drives the stale-translation check. */
    updated: string;
};

/** One task, collected from a `<!-- task: id — summary -->` marker under a How-to heading. */
export type HelpTask = {
    id: string;
    /** One-line outcome the user wants, not a UI action. */
    summary: string;
    /** Heading text the task sits under, for display. */
    heading: string;
    /** Anchor within the article, for deep links. */
    anchor: string;
    /** Article that documents it. */
    articleId: string;
};

/** An article as the Help Center consumes it. */
export type HelpArticle = HelpArticleMeta & {
    locale: string;
    /** Path below `docs/help/<locale>/`, identical across locales. */
    path: string;
    /** Raw markdown body, frontmatter stripped. */
    body: string;
    /** Heading text in document order, for search and in-article navigation. */
    headings: string[];
    /** Body reduced to plain text, for search. */
    text: string;
    /** Word count of the body, excluding frontmatter and task markers. */
    words: number;
    /**
     * Whole minutes to read, at 200 words per minute and never less than one.
     *
     * 200 rather than the usual 250 because these articles are dense — tables, numbered
     * steps, and figures people stop to check — and a reading time that reads low is
     * worse than none: it tells someone in a hurry they have time they do not have.
     */
    readingMinutes: number;
    tasks: HelpTask[];
};

/** The generated index: every article in every locale, plus the task catalogue. */
export type HelpIndex = {
    generatedAt: string;
    /** Locales that actually have at least one article. */
    locales: string[];
    articles: HelpArticle[];
    /** Tasks collected from the source locale only — translations reuse these ids. */
    tasks: HelpTask[];
};
